import {
  Button,
  computeBoardPosition,
  INVITED_PIPELINE_COLUMN_ID,
  INVITED_PIPELINE_STAGE,
  UNASSIGNED_PIPELINE_COLUMN_ID,
  UNASSIGNED_PIPELINE_STAGE,
  boardPipelineStages,
  groupPipelineApplicantsByStage,
  partitionPipelineApplicants,
  pipelineColumnSlug,
  cn,
} from '@yeshuman/sdk'
import type { BoardOrderEntry } from '@yeshuman/sdk'
import { useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ApplicantCard, type ApplicantAnalysisRetry, type ApplicantScoresGate } from './applicant-card'
import { StageColumnHeader } from './stage-column-header'
import { KANBAN_COLUMN_CLASS } from './simple-kanban'
import type { ApplicantCard as ApplicantCardModel, Stage } from '../../hooks/use-opportunity-applicants'

const KANBAN_COLUMN_OVERFLOW_LIMIT = 15

function stageDroppableId(stageSlug: string) {
  return `stage:${stageSlug}`
}

interface KanbanProps {
  stages: Stage[]
  applicants: ApplicantCardModel[]
  onMoveStage: (applicationId: string, stageSlug: string) => void
  onPersistOrder: (orderings: BoardOrderEntry[]) => void
  onOpenApplicant: (applicationId: string) => void
  onRecordDecision: (applicationId: string, decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: (applicationId: string) => void
  rankByApplicationId: Map<string, number>
  scoresGate?: ApplicantScoresGate
  analysisRetry?: ApplicantAnalysisRetry
  collapsedColumns: Set<string>
  onToggleColumnCollapse: (columnId: string) => void
  onOpenColumnInListView: (stageSlug: string) => void
  className?: string
}

export function ApplicantsBoardKanban({
  stages,
  applicants,
  onMoveStage,
  onPersistOrder,
  onOpenApplicant,
  onRecordDecision,
  onOpenThread,
  rankByApplicationId,
  scoresGate,
  analysisRetry,
  collapsedColumns,
  onToggleColumnCollapse,
  onOpenColumnInListView,
  className,
}: KanbanProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const skipOpenAfterDragIdRef = useRef<string | null>(null)

  const { invited, pipeline } = useMemo(() => partitionPipelineApplicants(applicants), [applicants])
  const byStage = useMemo(() => groupPipelineApplicantsByStage(pipeline, stages), [pipeline, stages])

  const unassignedApplicants = byStage.get(UNASSIGNED_PIPELINE_COLUMN_ID) ?? []

  const applicantById = useMemo(() => new Map(applicants.map((a) => [a.id, a])), [applicants])

  function markPostDragOpenSuppress(applicationId: string) {
    skipOpenAfterDragIdRef.current = applicationId
  }

  function handleOpenApplicantId(applicationId: string) {
    if (skipOpenAfterDragIdRef.current === applicationId) {
      skipOpenAfterDragIdRef.current = null
      return
    }
    onOpenApplicant(applicationId)
  }

  function columnApplicants(columnSlug: string): ApplicantCardModel[] {
    if (columnSlug === INVITED_PIPELINE_COLUMN_ID) return invited
    if (columnSlug === UNASSIGNED_PIPELINE_COLUMN_ID) return unassignedApplicants
    return byStage.get(columnSlug) ?? []
  }

  function resolveDropTarget(overId: string): { columnSlug: string; insertIndex: number } | null {
    if (overId.startsWith('stage:')) {
      const columnSlug = overId.slice('stage:'.length)
      return { columnSlug, insertIndex: columnApplicants(columnSlug).length }
    }

    const overApplicant = applicantById.get(overId)
    if (!overApplicant) return null
    const columnSlug = pipelineColumnSlug(overApplicant)
    const column = columnApplicants(columnSlug)
    const insertIndex = column.findIndex((entry) => entry.id === overId)
    return { columnSlug, insertIndex: insertIndex >= 0 ? insertIndex : column.length }
  }

  function handleDragStart(event: DragStartEvent) {
    setDraggingId(String(event.active.id))
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    const applicationId = String(active.id)

    if (!over) {
      setDraggingId(null)
      markPostDragOpenSuppress(applicationId)
      return
    }

    const activeApplicant = applicantById.get(applicationId)
    const dropTarget = resolveDropTarget(String(over.id))

    if (!activeApplicant || !dropTarget) {
      setDraggingId(null)
      markPostDragOpenSuppress(applicationId)
      return
    }

    const { columnSlug: targetColumnSlug, insertIndex } = dropTarget
    if (targetColumnSlug === INVITED_PIPELINE_COLUMN_ID) {
      setDraggingId(null)
      markPostDragOpenSuppress(applicationId)
      return
    }

    const sourceColumnSlug = pipelineColumnSlug(activeApplicant)
    const targetColumnItems = columnApplicants(targetColumnSlug).filter((entry) => entry.id !== applicationId)
    const boardPosition = computeBoardPosition(targetColumnItems, insertIndex)

    if (sourceColumnSlug !== targetColumnSlug) {
      onMoveStage(applicationId, targetColumnSlug)
    }

    onPersistOrder([{ application_id: applicationId, board_position: boardPosition }])

    setDraggingId(null)
    markPostDragOpenSuppress(applicationId)
  }

  const draggingApplicant = draggingId ? applicantById.get(draggingId) ?? null : null

  const sharedCardProps = {
    stages,
    onOpenApplicant: handleOpenApplicantId,
    onMoveStage,
    onRecordDecision,
    onOpenThread,
    rankByApplicationId,
    scoresGate,
    analysisRetry,
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={(event: DragCancelEvent) => {
        setDraggingId(null)
        markPostDragOpenSuppress(String(event.active.id))
      }}
    >
      <div className={cn('flex h-full min-h-0 flex-col', className)}>
        <div className="flex min-h-0 flex-1 items-stretch gap-4 overflow-x-auto pb-3">
          <InvitedColumn
            applicants={invited}
            collapsed={collapsedColumns.has(INVITED_PIPELINE_COLUMN_ID)}
            onToggleCollapse={() => onToggleColumnCollapse(INVITED_PIPELINE_COLUMN_ID)}
            onOpenOverflowInListView={() => onOpenColumnInListView('invited')}
            {...sharedCardProps}
          />
          {boardPipelineStages(stages).map((stage) => {
            const list = byStage.get(stage.slug) ?? []
            return (
              <PipelineColumn
                key={stage.slug}
                stage={stage}
                applicants={list}
                collapsed={collapsedColumns.has(stage.slug)}
                onToggleCollapse={() => onToggleColumnCollapse(stage.slug)}
                onOpenOverflowInListView={() => onOpenColumnInListView(stage.slug)}
                droppableId={stageDroppableId(stage.slug)}
                {...sharedCardProps}
              />
            )
          })}
          {unassignedApplicants.length > 0 ? (
            <PipelineColumn
              stage={UNASSIGNED_PIPELINE_STAGE}
              applicants={unassignedApplicants}
              collapsed={collapsedColumns.has(UNASSIGNED_PIPELINE_COLUMN_ID)}
              onToggleCollapse={() => onToggleColumnCollapse(UNASSIGNED_PIPELINE_COLUMN_ID)}
              onOpenOverflowInListView={() => onOpenColumnInListView(UNASSIGNED_PIPELINE_COLUMN_ID)}
              droppableId={stageDroppableId(UNASSIGNED_PIPELINE_COLUMN_ID)}
              helperText="Missing stage data. Use Move to… on a card or refresh so the server can assign a stage."
              {...sharedCardProps}
            />
          ) : null}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {draggingApplicant ? (
          <ApplicantCard
            applicant={draggingApplicant}
            stages={stages}
            onOpen={() => {}}
            onMoveStage={() => {}}
            onRecordDecision={() => {}}
            isOverlay
            className="w-[260px]"
            rank={rankByApplicationId.get(draggingApplicant.id)}
            scoresGate={scoresGate}
            analysisRetry={analysisRetry}
            commitment={draggingApplicant.status === 'invited' ? 'invited' : 'applied'}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

type SharedColumnCardProps = {
  stages: Stage[]
  onOpenApplicant: (applicationId: string) => void
  onMoveStage: (applicationId: string, stageSlug: string) => void
  onRecordDecision: (applicationId: string, decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: (applicationId: string) => void
  rankByApplicationId: Map<string, number>
  scoresGate?: ApplicantScoresGate
  analysisRetry?: ApplicantAnalysisRetry
}

function ColumnOverflowFooter({
  hiddenCount,
  onOpenInListView,
}: {
  hiddenCount: number
  onOpenInListView: () => void
}) {
  if (hiddenCount <= 0) return null
  return (
    <div className="shrink-0 border-t border-dashed border-border/60 px-3 py-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto w-full justify-center whitespace-normal px-2 py-2 text-xs text-muted-foreground"
        onClick={onOpenInListView}
      >
        {hiddenCount} more · open in table
      </Button>
    </div>
  )
}

function InvitedColumn({
  applicants,
  collapsed,
  onToggleCollapse,
  onOpenOverflowInListView,
  stages,
  onOpenApplicant,
  onMoveStage,
  onRecordDecision,
  onOpenThread,
  rankByApplicationId,
  scoresGate,
  analysisRetry,
}: {
  applicants: ApplicantCardModel[]
  collapsed: boolean
  onToggleCollapse: () => void
  onOpenOverflowInListView: () => void
} & SharedColumnCardProps) {
  const invitedStage = INVITED_PIPELINE_STAGE
  const visibleApplicants = applicants.slice(0, KANBAN_COLUMN_OVERFLOW_LIMIT)
  const hiddenCount = Math.max(0, applicants.length - visibleApplicants.length)

  if (collapsed) {
    return (
      <section
        className={cn(
          KANBAN_COLUMN_CLASS,
          'w-10 cursor-pointer border border-dashed border-border/60',
        )}
        aria-label="Invited column collapsed"
        onClick={onToggleCollapse}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggleCollapse()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="flex h-full flex-col items-center justify-between gap-2 px-1 py-3">
          <span
            className="text-xs font-semibold text-muted-foreground [writing-mode:vertical-rl] rotate-180"
            aria-hidden
          >
            Invited
          </span>
          <span className="text-xs font-medium tabular-nums text-muted-foreground">{applicants.length}</span>
        </div>
      </section>
    )
  }

  return (
    <section
      className={cn(KANBAN_COLUMN_CLASS, 'border border-dashed border-border/60')}
      aria-label="Invited column"
    >
      <StageColumnHeader
        stage={invitedStage}
        count={applicants.length}
        subtitle="not yet applied"
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div className="space-y-2 px-4 py-3">
          {visibleApplicants.length === 0 ? (
            <div className="flex min-h-24 items-center justify-center rounded-md border border-dashed border-border/40 px-3 text-center text-xs text-muted-foreground">
              No one invited yet
            </div>
          ) : (
            visibleApplicants.map((applicant) => (
              <ApplicantCard
                key={applicant.id}
                applicant={applicant}
                stages={stages}
                commitment="invited"
                rank={rankByApplicationId.get(applicant.id)}
                scoresGate={scoresGate}
                analysisRetry={analysisRetry}
                onOpen={() => onOpenApplicant(applicant.id)}
                onMoveStage={(slug) => onMoveStage(applicant.id, slug)}
                onRecordDecision={(decision) => onRecordDecision(applicant.id, decision)}
                onOpenThread={onOpenThread ? () => onOpenThread(applicant.id) : undefined}
              />
            ))
          )}
        </div>
      </div>
      <ColumnOverflowFooter hiddenCount={hiddenCount} onOpenInListView={onOpenOverflowInListView} />
    </section>
  )
}

function PipelineColumn({
  stage,
  applicants,
  collapsed,
  onToggleCollapse,
  onOpenOverflowInListView,
  droppableId,
  helperText,
  stages,
  onOpenApplicant,
  onMoveStage,
  onRecordDecision,
  onOpenThread,
  rankByApplicationId,
  scoresGate,
  analysisRetry,
}: {
  stage: Stage
  applicants: ApplicantCardModel[]
  collapsed: boolean
  onToggleCollapse: () => void
  onOpenOverflowInListView: () => void
  droppableId: string
  helperText?: string
} & SharedColumnCardProps) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId })
  const visibleApplicants = applicants.slice(0, KANBAN_COLUMN_OVERFLOW_LIMIT)
  const hiddenCount = Math.max(0, applicants.length - visibleApplicants.length)
  const itemIds = visibleApplicants.map((applicant) => applicant.id)

  if (collapsed) {
    return (
      <section
        ref={setNodeRef}
        className={cn(
          KANBAN_COLUMN_CLASS,
          'w-10 cursor-pointer border border-border/40 transition-colors',
          isOver && 'border-foreground/30 bg-muted/30',
        )}
        aria-label={`${stage.name} column collapsed`}
        onClick={onToggleCollapse}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggleCollapse()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="flex h-full flex-col items-center justify-between gap-2 px-1 py-3">
          <span
            className="text-xs font-semibold text-muted-foreground [writing-mode:vertical-rl] rotate-180"
            aria-hidden
          >
            {stage.name}
          </span>
          <span className="text-xs font-medium tabular-nums text-muted-foreground">{applicants.length}</span>
        </div>
      </section>
    )
  }

  return (
    <section
      ref={setNodeRef}
      className={cn(
        KANBAN_COLUMN_CLASS,
        'border border-border/40 transition-colors',
        isOver && 'border-foreground/30 bg-muted/30',
      )}
      aria-label={`${stage.name} column`}
    >
      <StageColumnHeader
        stage={stage}
        count={applicants.length}
        isOver={isOver}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      />
      {helperText ? (
        <p className="shrink-0 border-b border-border/40 px-2 pb-2 text-xs leading-snug text-muted-foreground">
          {helperText}
        </p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 px-4 py-3">
            {visibleApplicants.length === 0 ? (
              <div className="flex min-h-24 items-center justify-center rounded-md border border-dashed border-border/40 px-3 text-center text-xs text-muted-foreground">
                No one at this stage yet
              </div>
            ) : (
              visibleApplicants.map((applicant) => (
                <SortableApplicantCard
                  key={applicant.id}
                  applicant={applicant}
                  stages={stages}
                  rank={rankByApplicationId.get(applicant.id)}
                  scoresGate={scoresGate}
                  analysisRetry={analysisRetry}
                  onOpen={() => onOpenApplicant(applicant.id)}
                  onMoveStage={(slug) => onMoveStage(applicant.id, slug)}
                  onRecordDecision={(decision) => onRecordDecision(applicant.id, decision)}
                  onOpenThread={onOpenThread ? () => onOpenThread(applicant.id) : undefined}
                />
              ))
            )}
          </div>
        </SortableContext>
      </div>
      <ColumnOverflowFooter hiddenCount={hiddenCount} onOpenInListView={onOpenOverflowInListView} />
    </section>
  )
}

function SortableApplicantCard({
  applicant,
  stages,
  onOpen,
  onMoveStage,
  onRecordDecision,
  onOpenThread,
  rank,
  scoresGate,
  analysisRetry,
}: {
  applicant: ApplicantCardModel
  stages: Stage[]
  onOpen: () => void
  onMoveStage: (stageSlug: string) => void
  onRecordDecision: (decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: () => void
  rank?: number
  scoresGate?: ApplicantScoresGate
  analysisRetry?: ApplicantAnalysisRetry
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: applicant.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('transition-none', isDragging && 'z-20 opacity-40 touch-none')}
    >
      <ApplicantCard
        applicant={applicant}
        stages={stages}
        onOpen={onOpen}
        onMoveStage={onMoveStage}
        onRecordDecision={onRecordDecision}
        onOpenThread={onOpenThread}
        dragHandleProps={{ ...listeners, ...attributes }}
        rank={rank}
        scoresGate={scoresGate}
        analysisRetry={analysisRetry}
      />
    </div>
  )
}
